import {finalize} from 'rxjs/operators';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BannerNotificationsService, PaginatedList, SpinnerService } from '../../../xform-compat';
import { PortfolioService, ExchangeService, TenantService } from '../../../core/data-services';
import { EventsHubService } from '../../../core/events-hub.service';
import { Portfolio, Tenant } from '../../../shared/models';
import { CONFIG } from '../../../config';

@Component({
    selector: 'rms-add-portfolio',
    templateUrl: './add-portfolio.component.html',
    styleUrls: ['./add-portfolio.component.scss'],
    standalone: false
})
export class AddPortfolioComponent implements OnDestroy, OnInit {
  @ViewChild('addPortfolioModal', { static: true }) modal: ElementRef;
  private modalReference: NgbModalRef;
  private modalSubscription: Subscription;
  private largePageSize = CONFIG.pagination.singleLargePage;
  public tenantList: PaginatedList<Tenant>;
  public committedCash: number;
  public fidelityCash: number;
  public unfundedValue: number;
  public tenantId: number;
  public portfolioType: string;
  public name: string;

  public readonly STANDARD = 'STANDARD';
  public readonly VENTURE = 'VENTURE';
  public readonly SLEEVE = 'SLEEVE';

  constructor(
    private portfolioService: PortfolioService,
    private exchangeService: ExchangeService,
    private tenantService: TenantService,
    private spinnerService: SpinnerService,
    private bannerNotificationService: BannerNotificationsService,
    private translateService: TranslateService,
    private eventsHubService: EventsHubService,
    private modalService: NgbModal
  ) {
    this.committedCash = 0;
    this.fidelityCash = 0;
    this.unfundedValue = 0;
    this.name = '';
    this.portfolioType = this.STANDARD;
  }

  /**
   * On Init, get the exchanges list and subscribes to the create company modal
   */
  ngOnInit() {
    this.modalSubscription = this.eventsHubService.onAddPortfolioModalOpens$.subscribe(
      () => {
        this.committedCash = 0;
        this.fidelityCash = 0;
        this.unfundedValue = 0;
        this.name = '';
        this.portfolioType = this.STANDARD;
        this.modalReference = this.modalService.open(this.modal, {size: 'lg'});
        this.modalReference.result.then(
          () => {
            this.committedCash = 0;
            this.fidelityCash = 0;
            this.unfundedValue = 0;
            this.name = '';
            this.portfolioType = this.STANDARD;
          },
          () => {
            this.committedCash = 0;
            this.fidelityCash = 0;
            this.unfundedValue = 0;
            this.name = '';
            this.portfolioType = this.STANDARD;
          });
        this.getTenants();
      }
    );
  }

  /**
   * Unsubscribe on destroy component
   */
  ngOnDestroy () {
    this.modalSubscription.unsubscribe();
  }

  /**
   * Creates a new company
   * @param {NgForm} form
   */
  public createRecord (form: NgForm) {
    if (form.invalid) {
      return;
    }
    this.spinnerService.start();
    const body: Portfolio = form.value as Portfolio;
    body.fidelityCash = this.fidelityCash;
    body.committedCash = this.committedCash;
    body.unfundedValue = this.unfundedValue;
    this.portfolioService.addPortfolio(form.value).pipe(
      finalize(() => this.spinnerService.stop()))
      .subscribe(
        () => {
          this.bannerNotificationService.success(this.translateService.instant('MESSAGES.CREATED', {value: 'Portfolio'}));
          form.reset();
          this.modalReference.close();
        },
        error => this.bannerNotificationService.error(error)
      );
  }

  /**
   * Populates the tenants drop down in the form component.
   * RA is the default value.
   */
   private getTenants () {
    this.tenantService.getRecords({pagesize: this.largePageSize}).subscribe(
      value => {
        const defautTenant = value.records.find( tenant => tenant.id === CONFIG.defaultTenantId);
        this.tenantList = value;
        this.tenantId = defautTenant ? defautTenant.id : value.records[0].id;
      },
      error => this.bannerNotificationService.error(error)
    );
  }
}
